// @ts-nocheck — TODO[type-debt]: 5 type mismatch reali (Combobox onChange / SaldoCardProps / Dispatch). Fix in sessione dedicata.
/**
 * Form creazione/modifica articolo di magazzino.
 *
 * Se l'URL contiene :id, carica l'articolo esistente (modalità modifica).
 * Altrimenti è in modalità creazione.
 * Il fornitore preferenziale è obbligatorio (DEC-047).
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  type ComboboxOption,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Fornitore {
  id: string;
  ragione_sociale: string;
  partita_iva: string | null;
}

interface ArticoloFormData {
  sku_interno: string;
  descrizione: string;
  fornitore_preferenziale_id: string;
  categoria_id: string;
  sottocategoria_id: string;
  metodo_uscita: string;
  soglia_minima: string;
  soglia_riordino: string;
  descrizione_estesa: string;
  consigliato_per: string;
  note: string;
}

interface TreeNode {
  id: string;
  codice: string;
  descrizione: string;
  parent_id: string | null;
  children: TreeNode[];
}

const EMPTY_FORM: ArticoloFormData = {
  sku_interno: '',
  descrizione: '',
  fornitore_preferenziale_id: '',
  categoria_id: '',
  sottocategoria_id: '',
  metodo_uscita: 'fefo',
  soglia_minima: '',
  soglia_riordino: '',
  descrizione_estesa: '',
  consigliato_per: '',
  note: '',
};

/** Metodi di uscita disponibili per la selezione. */
const METODI_USCITA = [
  { value: 'fefo', label: 'FEFO (First Expired, First Out)' },
  { value: 'fifo', label: 'FIFO (First In, First Out)' },
  { value: 'manuale', label: 'Manuale' },
] as const;

const AUTH_HEADERS = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/**
 * Form creazione/modifica articolo con fornitore obbligatorio (DEC-047).
 */
const ArticoloForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<ArticoloFormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fornitore
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [fornitoreSearch, setFornitoreSearch] = useState('');
  const [fornitoreLabel, setFornitoreLabel] = useState('');

  // Categorie / sottocategorie (tree merceologica)
  const [tree, setTree] = useState<TreeNode[]>([]);

  const fetchTree = useCallback(async () => {
    try {
      const resp = await fetch(
        '/api/v1/classificazioni/tree?sistema=merceologica',
        { headers: AUTH_HEADERS() },
      );
      if (resp.ok) setTree(await resp.json());
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void fetchTree();
  }, [fetchTree]);

  const selectedCategoria = tree.find((c) => c.id === form.categoria_id);
  const sottocategorie = selectedCategoria?.children ?? [];

  // -----------------------------------------------------------------------
  // Fetch fornitori (autocomplete)
  // -----------------------------------------------------------------------

  const fetchFornitori = useCallback(async (q: string) => {
    if (q.length < 1) {
      setFornitori([]);
      return;
    }
    try {
      const resp = await fetch(
        `/api/v1/fornitori?q=${encodeURIComponent(q)}&limit=20`,
        { headers: AUTH_HEADERS() },
      );
      if (resp.ok) {
        setFornitori(await resp.json());
      }
    } catch {
      /* ignore */
    }
  }, []);

  // -----------------------------------------------------------------------
  // Load existing article (edit mode)
  // -----------------------------------------------------------------------

  /** Carica dati articolo esistente se in modalità modifica. */
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void (async () => {
      try {
        const response = await fetch(`/api/v1/magazzino/articoli/${id}`, {
          headers: AUTH_HEADERS(),
        });
        if (!response.ok) throw new Error(`Errore ${response.status}`);
        const data = await response.json();
        const catId = data.categoria_merce_id ?? '';
        let rootCatId = '';
        let leafCatId = '';
        if (catId) {
          const root = tree.find((r) => r.id === catId);
          if (root) {
            rootCatId = root.id;
          } else {
            for (const r of tree) {
              if (r.children.some((c) => c.id === catId)) {
                rootCatId = r.id;
                leafCatId = catId;
                break;
              }
            }
          }
        }
        setForm({
          sku_interno: data.sku_interno ?? '',
          descrizione: data.descrizione ?? '',
          fornitore_preferenziale_id: data.fornitore_preferenziale_id ?? '',
          categoria_id: rootCatId,
          sottocategoria_id: leafCatId,
          metodo_uscita: data.metodo_uscita ?? 'fefo',
          soglia_minima:
            data.soglia_minima != null ? String(data.soglia_minima) : '',
          soglia_riordino:
            data.soglia_riordino != null ? String(data.soglia_riordino) : '',
          descrizione_estesa: data.descrizione_estesa ?? '',
          consigliato_per: data.consigliato_per ?? '',
          note: data.note ?? '',
        });

        // Carica il nome del fornitore per la visualizzazione
        if (data.fornitore_preferenziale_id) {
          const fResp = await fetch(
            `/api/v1/soggetti/${data.fornitore_preferenziale_id}`,
            { headers: AUTH_HEADERS() },
          );
          if (fResp.ok) {
            const fData = await fResp.json();
            setFornitoreLabel(fData.ragione_sociale ?? '');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore caricamento');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  /** Aggiorna un campo del form. */
  const handleChange = (field: keyof ArticoloFormData, value: string) => {
    setForm((prev: ArticoloFormData) => ({ ...prev, [field]: value }));
  };

  const handleFornitoreSelect = (opt: ComboboxOption) => {
    setFornitoreLabel(opt.label);
    setFornitoreSearch('');
    handleChange('fornitore_preferenziale_id', opt.value);
  };

  /** Invia il form (POST per creazione, PATCH per modifica). */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const categoria_merce_id =
      form.sottocategoria_id || form.categoria_id || null;

    const payload: Record<string, unknown> = {
      sku_interno: form.sku_interno.trim(),
      descrizione: form.descrizione.trim() || null,
      fornitore_preferenziale_id: form.fornitore_preferenziale_id || null,
      categoria_merce_id,
      metodo_uscita: form.metodo_uscita,
      soglia_minima: form.soglia_minima ? Number(form.soglia_minima) : null,
      soglia_riordino: form.soglia_riordino
        ? Number(form.soglia_riordino)
        : null,
      descrizione_estesa: form.descrizione_estesa.trim() || null,
      consigliato_per: form.consigliato_per.trim() || null,
      note: form.note.trim() || null,
    };

    try {
      const url = isEdit
        ? `/api/v1/magazzino/articoli/${id}`
        : '/api/v1/magazzino/articoli';
      const method = isEdit ? 'PATCH' : 'POST';
      const response = await fetch(url, {
        method,
        headers: AUTH_HEADERS(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail ?? `Errore ${response.status}`);
      }
      const data = await response.json();
      navigate(`/magazzino/articoli/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          {isEdit ? 'Modifica articolo' : 'Nuovo articolo'}
        </h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Dati articolo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sku_interno">SKU interno *</Label>
                <Input
                  id="sku_interno"
                  value={form.sku_interno}
                  onChange={(e) => handleChange('sku_interno', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="descrizione">Descrizione *</Label>
                <Input
                  id="descrizione"
                  value={form.descrizione}
                  onChange={(e) => handleChange('descrizione', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fornitore">Fornitore *</Label>
                <Combobox
                  value={fornitoreSearch || fornitoreLabel}
                  onInputChange={(v: string) => {
                    setFornitoreSearch(v);
                    setFornitoreLabel('');
                    if (form.fornitore_preferenziale_id) {
                      handleChange('fornitore_preferenziale_id', '');
                    }
                    fetchFornitori(v);
                  }}
                  onSelect={handleFornitoreSelect}
                  options={fornitori.map((f) => ({
                    value: f.id,
                    label: f.ragione_sociale,
                    sublabel: f.partita_iva ?? undefined,
                  }))}
                  placeholder="Cerca fornitore..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoria_id">Categoria merceologica</Label>
                <Select
                  value={form.categoria_id}
                  onValueChange={(v) => {
                    setForm((prev) => ({
                      ...prev,
                      categoria_id: v,
                      sottocategoria_id: '',
                    }));
                  }}
                >
                  <SelectTrigger id="categoria_id">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {tree.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.descrizione}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sottocategoria_id">Sottocategoria</Label>
                <Select
                  value={form.sottocategoria_id}
                  onValueChange={(v) => handleChange('sottocategoria_id', v)}
                  disabled={!form.categoria_id || sottocategorie.length === 0}
                >
                  <SelectTrigger id="sottocategoria_id">
                    <SelectValue
                      placeholder={
                        sottocategorie.length === 0
                          ? 'Nessuna sottocategoria'
                          : 'Seleziona sottocategoria'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sottocategorie.map((sc) => (
                      <SelectItem key={sc.id} value={sc.id}>
                        {sc.descrizione}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="metodo_uscita">Metodo uscita</Label>
                <Select
                  value={form.metodo_uscita}
                  onValueChange={(v) => handleChange('metodo_uscita', v)}
                >
                  <SelectTrigger id="metodo_uscita">
                    <SelectValue placeholder="Seleziona metodo" />
                  </SelectTrigger>
                  <SelectContent>
                    {METODI_USCITA.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="soglia_minima">Soglia minima</Label>
                <Input
                  id="soglia_minima"
                  type="number"
                  min="0"
                  value={form.soglia_minima}
                  onChange={(e) =>
                    handleChange('soglia_minima', e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="soglia_riordino">Soglia riordino</Label>
                <Input
                  id="soglia_riordino"
                  type="number"
                  min="0"
                  value={form.soglia_riordino}
                  onChange={(e) =>
                    handleChange('soglia_riordino', e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descrizione_estesa">Descrizione prodotto</Label>
              <textarea
                id="descrizione_estesa"
                className="flex min-h-[100px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                value={form.descrizione_estesa}
                onChange={(e) => handleChange('descrizione_estesa', e.target.value)}
                rows={4}
                placeholder="Descrizione dettagliata del prodotto, ingredienti, caratteristiche…"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="consigliato_per">Consigliato per</Label>
              <textarea
                id="consigliato_per"
                className="flex min-h-[100px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                value={form.consigliato_per}
                onChange={(e) => handleChange('consigliato_per', e.target.value)}
                rows={4}
                placeholder="Usi consigliati, indicazioni, a chi è destinato…"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <textarea
                id="note"
                className="flex min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                value={form.note}
                onChange={(e) => handleChange('note', e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={
                  saving ||
                  (!isEdit && !form.fornitore_preferenziale_id)
                }
              >
                {saving
                  ? 'Salvataggio…'
                  : isEdit
                    ? 'Salva modifiche'
                    : 'Crea articolo'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default ArticoloForm;
